import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Clock3 } from 'lucide-react';
import SyncedEmailList from '../../shared/SyncedEmailList';
import FilterDrawer from '../../shared/FilterDrawer';
import FilterChips from '../../shared/FilterChips';
import ToggleSwitch from '../../shared/ToggleSwitch';
import {
  useElementWidth, topRoundedBar, SegmentedToggle, ChartHoverBox,
  MAX_BAR_WIDTH, STACK_GAP, BASELINE_COLOR, HIGHLIGHT_COLOR, loadingFade,
} from '../../shared/DashboardChart';
import { useStaffAuth } from '../../shared/StaffAuthContext';
import { formatTimeAU, formatDate, formatDateWords, todayInTenant, formatWeekdayShort, formatMonthName } from '../../shared/tenantClock';
import { ACTIVITY_TYPE_LABELS, ACTIVITY_TYPE_ICONS } from './Leads/activityConstants';

// CRM Dashboard (issue #259): three summary cards side by side. Synced
// Email summarises what Email Sync has logged across every record, with the
// full list one click away (Review emails), so it can be reviewed in one
// place rather than only turning up on each record's Activities.
// Same dashboard-page-* shell as SalesDashboard.js. Pipeline and Upcoming
// are simple live summaries of existing data, each linking through to
// where the detail lives.

const PAGE_SIZE = 25;
const UPCOMING_DAYS = 14;
// The Activities chart's bar height; the day labels sit below it.
const ACTIVITY_BAR_HEIGHT = 130;

const CARD_STYLE = { background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', boxShadow: 'var(--shadow-sm)', padding: 16 };
// A card's section stretches to its grid cell and the card fills it, so
// cards side by side in a row share one height.
const SECTION_STYLE = { marginBottom: 0, display: 'flex', flexDirection: 'column' };
const FILL = { flex: 1 };
const EMPTY_STYLE = { fontSize: 14, color: '#888' };
const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #e3e7ee', borderRadius: 6, fontSize: 13, minWidth: 0 };
const ELLIPSIS = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function addDays(dateOnly, days) {
  const d = new Date(`${dateOnly}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Every open Prospect, page by page (the list endpoint caps a page at 100),
// same as CustomerPipelinePage.js's board.
async function fetchAllProspects(params) {
  const cards = [];
  for (let page = 1; ; page += 1) {
    const res = await axios.get('/api/commercial-entities', { params: { status: 'active', limit: 100, page, ...params } });
    cards.push(...(res.data.data || []));
    if (page >= (res.data.pagination?.pages || 1)) return cards;
  }
}

// Open Prospects per open Pipeline Stage, plus closed Prospects due for a
// revisit. Clicking it opens the Pipeline board.
function PipelineCard({ refreshKey }) {
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [revisitCount, setRevisitCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get('/api/relationship-types', { params: { status: 'active' } }),
      fetchAllProspects({}),
      fetchAllProspects({ revisit: true }),
    ])
      .then(([typesRes, prospects, revisits]) => {
        const prospectType = (typesRes.data.data || []).find((t) => t.system_key === 'prospect');
        const stageOf = (e) => (e.relationships || []).find((r) => r.relationship_type?.system_key === 'prospect')?.relationship_stage_id;
        setStages((prospectType?.stages || [])
          .filter((st) => st.is_open && st.is_active)
          .map((st) => ({ id: st.id, name: st.name, count: prospects.filter((e) => stageOf(e) === st.id).length })));
        setRevisitCount(revisits.length);
      })
      .catch(() => { setStages([]); setRevisitCount(0); })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const total = stages.reduce((sum, st) => sum + st.count, 0);
  const max = Math.max(1, ...stages.map((st) => st.count));

  let content;
  if (loading) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else if (stages.length === 0) content = <div style={EMPTY_STYLE}>No open Pipeline Stages set up.</div>;
  else {
    content = (
      <>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>{total} open Prospect{total === 1 ? '' : 's'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stages.map((st) => (
            <div key={st.id} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 32px', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={ELLIPSIS}>{st.name}</span>
              <div style={{ background: '#eef2f7', borderRadius: 4, height: 10 }}>
                <div style={{ width: `${(st.count / max) * 100}%`, background: '#1976d2', borderRadius: 4, height: 10 }} />
              </div>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>{st.count}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: revisitCount ? '#8a5a00' : '#888' }}>
          {revisitCount} closed Prospect{revisitCount === 1 ? '' : 's'} due for a revisit
        </div>
      </>
    );
  }

  return (
    <div className="metrics-section" style={SECTION_STYLE}>
      <h2>Pipeline</h2>
      <div style={{ ...CARD_STYLE, ...FILL, cursor: 'pointer' }} onClick={() => navigate('/crm-customer-pipeline')} title="Open the Pipeline board">
        {content}
      </div>
    </div>
  );
}

// Meetings, Calls and open Tasks over the next two weeks, soonest first.
// The card opens the Home Dashboard's Calendar; a row opens its record.
function UpcomingCard({ refreshKey }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const from = todayInTenant();
    setLoading(true);
    axios.get('/api/activities/calendar', { params: { from, to: addDays(from, UPCOMING_DAYS) } })
      .then((res) => setRows((res.data.data || []).filter((a) => !a.is_done)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  let content;
  if (loading) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else if (rows.length === 0) content = <div style={EMPTY_STYLE}>Nothing coming up.</div>;
  else {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((a) => {
          const Icon = ACTIVITY_TYPE_ICONS[a.activity_type];
          return (
            <div
              key={a.id}
              onClick={(e) => { if (!a.commercial_entity) return; e.stopPropagation(); navigate(`/commercial-entities/${a.commercial_entity.id}`); }}
              title={a.commercial_entity ? `Open ${a.commercial_entity.name}` : undefined}
              style={{ ...ROW_STYLE, background: '#fff' }}
            >
              {Icon && <Icon size={14} strokeWidth={1.8} style={{ color: '#5a6b85', flexShrink: 0 }} aria-label={ACTIVITY_TYPE_LABELS[a.activity_type]} />}
              <span style={{ ...ELLIPSIS, flex: 2, fontWeight: 600 }}>{a.topic || ACTIVITY_TYPE_LABELS[a.activity_type] || 'Activity'}</span>
              <span style={{ ...ELLIPSIS, flex: 1, color: '#555' }}>{a.commercial_entity?.name || '—'}</span>
              <span style={{ flexShrink: 0, fontSize: 12, color: '#888' }}>
                {formatDate(a.activity_date)}{a.start_time ? ` ${String(a.start_time).slice(0, 5)}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="metrics-section" style={SECTION_STYLE}>
      <h2>Upcoming</h2>
      <div
        style={{ ...CARD_STYLE, ...FILL, cursor: 'pointer' }}
        onClick={() => navigate('/')}
        title="Open the Calendar"
      >
        <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>Meetings, Calls and Tasks in the next {UPCOMING_DAYS} days</div>
        <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>{content}</div>
      </div>
    </div>
  );
}

const TABS_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid #b3bbc7', marginBottom: 12 };
const TAB_WRAP_STYLE = { display: 'flex', alignItems: 'center', gap: 2, paddingBottom: 8 };

function tabColors(selected) {
  return {
    fontWeight: selected ? 700 : 400,
    color: selected ? '#222' : '#666',
    borderBottom: selected ? '2px solid #000' : '2px solid transparent',
  };
}

// My emails / All emails. My emails needs someone signed in; shared
// app-password access only has All emails.
function ScopeTabs({ scope, onChange, extra, noun = 'emails' }) {
  const { currentUser } = useStaffAuth();
  const tabs = [
    ...(currentUser ? [{ key: 'mine', label: `My ${noun}` }] : []),
    { key: 'everyone', label: `All ${noun}` },
  ];
  return (
    <div style={TABS_ROW_STYLE}>
      {tabs.map((tab) => (
        <div key={tab.key} style={{ ...TAB_WRAP_STYLE, ...tabColors(scope === tab.key) }}>
          <button
            type="button"
            onClick={() => onChange(tab.key)}
            title={tab.key === 'mine' ? `Show ${noun} you logged` : `Show ${noun} logged by anyone`}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', fontWeight: 'inherit', fontSize: 13 }}
          >
            {tab.label}
          </button>
        </div>
      ))}
      {extra && <span style={{ marginLeft: 'auto', paddingBottom: 8, fontSize: 13, color: '#666' }}>{extra}</span>}
    </div>
  );
}

// Week / Fortnight / Month, the same words and look as the Calendar's own
// view toggle, here meaning the last 7, 14 or 30 days.
const PERIODS = [
  { days: 7, label: 'Week' },
  { days: 14, label: 'Fortnight' },
  { days: 30, label: 'Month' },
];

function PeriodToggle({ value, onChange }) {
  return (
    <SegmentedToggle
      options={PERIODS.map((p) => ({ key: p.days, label: p.label, title: `Show the last ${p.days} days` }))}
      value={value}
      onChange={onChange}
    />
  );
}

// Emails logged per day over the period, each bar split into received
// (green, bottom) and sent (blue, top), one slot per day across the
// column's width. A day's total sits above its bar while bars are wide
// enough to read it; hovering a bar shows a box with its date and split.
// Bar shape, width, baseline, highlight and hover box are the shared
// dashboard chart pieces (shared/DashboardChart.js), same as the Sales
// Dashboard's Sales by Period.
// segments: what each day's bar is split into, bottom first, as
// [{ key, label, color }] where key names a count on each day (received /
// sent, email / call / meeting, or count for one colour). onDayClick(date):
// each day's whole column (not just its bar, so an empty day or a thin
// Month bar is easy to hit) highlights on hover and opens that day's list;
// selectedDate stays highlighted while its list is open.
function DailyChart({ daily, segments, onDayClick, selectedDate }) {
  const [ref, width] = useElementWidth();
  const [hoverIndex, setHoverIndex] = useState(null);
  const max = Math.max(1, ...daily.map((d) => d.count));
  const gap = daily.length > 14 ? 3 : 6;
  // Each day's slot, and its bar centred in it, no wider than the shared cap.
  const slotWidth = width ? Math.max(2, (width - (gap * (daily.length - 1))) / daily.length) : 0;
  const barWidth = Math.min(slotWidth, MAX_BAR_WIDTH);
  const showCounts = barWidth >= 14;
  // Three rows under the bars, like a printed calendar: the weekday ('Mon',
  // or 'M' when slots are narrow, as in Month), the day number, and the
  // month name only under the first bar and where the month changes. A
  // month name has no day/month order, so it suits any Date Format. Slots
  // too narrow for every label get one every 2nd or 3rd, always including
  // today at the right.
  const narrowDays = slotWidth < 30;
  const labelEvery = slotWidth >= 12 ? 1 : (slotWidth >= 7 ? 2 : 3);
  const barArea = ACTIVITY_BAR_HEIGHT;
  const labelArea = 14;
  const dateArea = 40;
  const height = barArea + labelArea;
  const slotX = (i) => i * (slotWidth + gap);

  const nameOf = (seg) => {
    const name = [seg.group, seg.label].filter(Boolean).join(' ');
    return name.charAt(0).toUpperCase() + name.slice(1);
  };
  const hovered = hoverIndex !== null ? daily[hoverIndex] : null;
  // The top segment of the bar is listed first.
  const tooltip = hovered ? (
    <ChartHoverBox
      chartWidth={width}
      anchorLeft={slotX(hoverIndex) - (gap / 2)}
      anchorRight={slotX(hoverIndex) + slotWidth + (gap / 2)}
      heading={formatDateWords(hovered.date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
      rows={[...segments].reverse().map((seg) => ({
        key: seg.key,
        color: seg.color,
        name: nameOf(seg),
        value: segments.length > 1 ? (Number(hovered[seg.key]) || 0) : hovered.count,
      }))}
      total={segments.length > 1 ? hovered.count : undefined}
      hint={onDayClick ? 'Click to see them' : undefined}
    />
  ) : null;

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height + dateArea} role="img" aria-label={`Logged per day, last ${daily.length} days`}>
          {daily.map((d, i) => {
            const x = slotX(i);
            const bx = x + ((slotWidth - barWidth) / 2);
            const cx = x + (slotWidth / 2);
            const total = d.count ? Math.max(3, (d.count / max) * barArea) : 0;
            // Each segment's share of the bar, stacked bottom first, with a
            // white gap between segments and the top one rounded.
            let stackTop = height;
            const pieces = segments.map((seg) => {
              const value = Number(d[seg.key]) || 0;
              const share = d.count ? total * (value / d.count) : 0;
              const below = stackTop < height;
              stackTop -= share;
              return { ...seg, value, y: stackTop, h: share > 0 ? Math.max(1, share - (below ? STACK_GAP : 0)) : 0 };
            });
            const topKey = [...pieces].reverse().find((p) => p.h > 0)?.key;
            const highlighted = hoverIndex === i || selectedDate === d.date;
            return (
              <g
                key={d.date}
                onClick={onDayClick ? () => onDayClick(d.date) : undefined}
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
                style={{ cursor: onDayClick ? 'pointer' : 'default' }}
              >
                <rect x={x - (gap / 2)} y={0} width={slotWidth + gap} height={height + dateArea} rx={4} fill={highlighted ? HIGHLIGHT_COLOR : 'transparent'} />
                {pieces.map((p) => p.h > 0 && (p.key === topKey
                  ? <path key={p.key} d={topRoundedBar(bx, p.y, barWidth, p.h)} fill={p.color} />
                  : <rect key={p.key} x={bx} y={p.y} width={barWidth} height={p.h} fill={p.color} />))}
                {showCounts && d.count > 0 && (
                  <text x={cx} y={height - total - 3} textAnchor="middle" fontSize={10} fill="#555">{d.count}</text>
                )}
                {(daily.length - 1 - i) % labelEvery === 0 && (
                  <text x={cx} y={height + 12} textAnchor="middle" fontSize={10} fill="#666" fontWeight={i === daily.length - 1 ? 700 : 400}>
                    <tspan x={cx}>{formatWeekdayShort(d.date, { narrow: narrowDays })}</tspan>
                    <tspan x={cx} dy={12}>{Number(d.date.slice(8, 10))}</tspan>
                  </text>
                )}
                {(i === 0 || d.date.endsWith('-01')) && (
                  <text x={x} y={height + 36} textAnchor="start" fontSize={10} fontWeight={600} fill="#555">{formatMonthName(d.date)}</text>
                )}
              </g>
            );
          })}
          <line x1={0} x2={width} y1={height + 0.5} y2={height + 0.5} stroke={BASELINE_COLOR} pointerEvents="none" />
        </svg>
      )}
      {tooltip}
    </div>
  );
}

// The drill-down behind the Activities card, in a right-side panel: the
// Emails, Calls or Meetings for a clicked day or the card's whole period
// (range: { from, to, label }), newest first, as compact rows that expand
// in place. An email synced from the viewer's own mailbox has Remove...;
// Load more brings in older ones.
function ActivityReviewDrawer({ open, onOpenChange, initialScope, type, range, onRemoved }) {
  const meta = ACTIVITY_TYPES.find((t) => t.key === type) || ACTIVITY_TYPES[0];
  const [scope, setScope] = useState(initialScope);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  useEffect(() => { if (open) setScope(initialScope); }, [open, initialScope]);

  const load = (nextPage, append) => {
    setLoading(true);
    axios.get('/api/activities/synced-emails', {
      params: { type, scope, from: range?.from, to: range?.to, page: nextPage, limit: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data.data || [];
        setRows((prev) => (append ? [...prev, ...data] : data));
        setTotal(res.data.pagination?.total ?? data.length);
        setPage(nextPage);
      })
      .catch(() => {
        if (!append) { setRows([]); setTotal(0); }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setNotice('');
    setRows([]);
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope, type, range?.from, range?.to]);

  const handleRemoved = (removed, _data, message) => {
    setRows((prev) => prev.filter((a) => a.id !== removed.id));
    setTotal((t) => Math.max(t - 1, 0));
    setNotice(message || '');
    onRemoved();
  };

  let content;
  if (loading && !rows.length) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else if (rows.length === 0) content = <div style={EMPTY_STYLE}>No {meta.noun} {range ? `for ${range.label}` : ''}.</div>;
  else {
    content = (
      <>
        <SyncedEmailList activities={rows} onRemoved={handleRemoved} showStaff={scope === 'everyone'} compact />
        {rows.length < total && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button
              type="button"
              className="dashboard-refresh-btn"
              disabled={loading}
              onClick={() => load(page + 1, true)}
              title={`Load the next older ${meta.noun}`}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`${type === 'all' ? 'Activities' : meta.label} · ${range?.label || ''}`}
      description={{
        all: 'Synced emails, Calls and Meetings, newest first. Click one to read it.',
        email: 'Emails logged by Email Sync, newest first. Click one to read it.',
      }[type] || `${meta.label} logged, newest first. Click one to read its notes.`}
      width="min(640px, 96vw)"
    >
      <ScopeTabs
        scope={scope}
        onChange={setScope}
        noun={meta.noun}
        extra={loading && !rows.length ? '' : `${total} ${total === 1 ? meta.noun.replace(/s$/, '') : meta.noun}`}
      />
      {notice && (
        <div style={{ marginBottom: 10, background: '#e3f1e4', border: '1px solid #b7dbb9', color: '#1b5e20', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
          {notice}
        </div>
      )}
      {content}
    </FilterDrawer>
  );
}

// The Activities card's All / Emails / Calls / Meetings pills (same chips
// as the Home Dashboard's focus filter). All is the three together, split
// by type; Emails are the ones Email Sync logged, split received/sent;
// Calls are logged Calls, split inbound/outbound; Meetings have no
// direction, so one colour.
// Calls and Meetings use the Calendar's colours for them.
// One colour per type, everywhere: teal for Emails; Calls and Meetings the
// Calendar's purple and blue. A type's direction split uses two shades of
// its own colour, darker for incoming and lighter for outgoing; a light
// shade's `text` is the darker one, so its total stays readable.
const EMAIL_COLOR = '#0d9488';
const CALL_COLOR = '#7b1fa2';
const MEETING_COLOR = '#1976d2';
const EMAIL_IN = '#0f766e';
const EMAIL_OUT = '#5eead4';
const CALL_IN = '#6a1b9a';
const CALL_OUT = '#ce93d8';
const ACTIVITY_TYPES = [
  {
    key: 'all',
    label: 'All',
    noun: 'activities',
    segments: [
      { key: 'email', label: 'emails', color: EMAIL_COLOR },
      { key: 'call', label: 'calls', color: CALL_COLOR },
      { key: 'meeting', label: 'meetings', color: MEETING_COLOR },
    ],
    // With the In / out switch on: by type and direction, so in/out shows
    // on the bars themselves.
    splitSegments: [
      { key: 'email_in', group: 'Emails', label: 'in', color: EMAIL_IN },
      { key: 'email_out', group: 'Emails', label: 'out', color: EMAIL_OUT, text: EMAIL_IN },
      { key: 'call_in', group: 'Calls', label: 'in', color: CALL_IN },
      { key: 'call_out', group: 'Calls', label: 'out', color: CALL_OUT, text: CALL_IN },
      { key: 'meeting', group: 'Meetings', label: '', color: MEETING_COLOR },
    ],
  },
  {
    key: 'email',
    label: 'Emails',
    noun: 'emails',
    segments: [{ key: 'count', label: 'emails', color: EMAIL_COLOR }],
    splitSegments: [{ key: 'received', label: 'received', color: EMAIL_IN }, { key: 'sent', label: 'sent', color: EMAIL_OUT, text: EMAIL_IN }],
  },
  {
    key: 'call',
    label: 'Calls',
    noun: 'calls',
    segments: [{ key: 'count', label: 'calls', color: CALL_COLOR }],
    splitSegments: [{ key: 'received', label: 'inbound', color: CALL_IN }, { key: 'sent', label: 'outbound', color: CALL_OUT, text: CALL_IN }],
  },
  { key: 'meeting', label: 'Meetings', noun: 'meetings', segments: [{ key: 'count', label: 'meetings', color: MEETING_COLOR }] },
];

// Activities summary: pick All, Emails, Calls or Meetings, mine or everyone's,
// over a Week / Fortnight / Month. Today and period totals, a bar per day,
// and the busiest contacts. Clicking a day, or View all for the period,
// opens the list in a side panel.
function ActivitiesCard({ refreshKey }) {
  const navigate = useNavigate();
  const { currentUser } = useStaffAuth();
  const [type, setType] = useState('all');
  const [scope, setScope] = useState(currentUser ? 'mine' : 'everyone');
  const [period, setPeriod] = useState(14);
  // The In / out switch: off shows one colour per type, on splits Emails
  // and Calls into in and out. One setting for every pill and period.
  const [showInOut, setShowInOut] = useState(false);
  const [summary, setSummary] = useState(null);
  const [typeTotals, setTypeTotals] = useState({});
  const [loading, setLoading] = useState(true);
  // The open drill-down: { from, to, label } for a clicked day or the whole
  // period, or null when closed.
  const [drill, setDrill] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get('/api/activities/summary', { params: { type, scope, days: period } })
      .then((res) => {
        if (cancelled) return;
        setSummary(res.data.data);
        setTypeTotals(res.data.type_totals || {});
      })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, scope, period, refreshKey, reloadKey]);

  const meta = ACTIVITY_TYPES.find((t) => t.key === type);
  const segments = showInOut && meta.splitSegments ? meta.splitSegments : meta.segments;
  const columnLabel = { fontSize: 12, color: '#666', marginBottom: 8 };

  let content;
  if (loading && !summary) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else if (!summary) content = <div style={EMPTY_STYLE}>Could not load the summary.</div>;
  else {
    content = (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'flex-start', ...loadingFade(loading) }}>
        {/* Two columns: the chart takes the wider share, Busiest contacts
            the rest; they stack on a narrow screen. */}
        <div style={{ flex: '2 1 420px', minWidth: 0 }}>
          {/* Every total sits on the heading line, leaving the height to the
              chart; the coloured split totals double as its legend. */}
          <div style={{ ...columnLabel, display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
            <span>{type === 'all' ? 'Activities logged' : `${meta.label} logged`}</span>
            <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{summary.today}</strong> today</span>
            <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{summary.total}</strong> last {summary.days} days</span>
            {segments.length > 1 && (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {segments.map((seg, i) => {
                  const newGroup = seg.group && seg.group !== segments[i - 1]?.group;
                  return (
                    <span key={seg.key} style={{ color: seg.text || seg.color, marginLeft: newGroup && i > 0 ? 6 : 0 }}>
                      {newGroup && <span style={{ color: '#666', marginRight: 6 }}>{seg.group}</span>}
                      <span style={{ color: seg.color }}>■</span> <strong style={{ fontSize: 15 }}>{summary.daily.reduce((sum, d) => sum + (Number(d[seg.key]) || 0), 0)}</strong>{seg.label ? ` ${seg.label}` : ''}
                    </span>
                  );
                })}
              </span>
            )}
          </div>
          <div style={{ marginTop: 4 }}>
            <DailyChart
              daily={summary.daily}
              segments={segments}
              onDayClick={(date) => setDrill({ from: date, to: date, label: formatDateWords(date, { weekday: 'short', day: 'numeric', month: 'short' }) })}
              selectedDate={drill && drill.from === drill.to ? drill.from : null}
            />
          </div>
        </div>

        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={columnLabel}>Busiest contacts</div>
          {summary.busiest.length === 0 ? (
            <div style={{ fontSize: 13, color: '#888' }}>None yet.</div>
          ) : summary.busiest.map((r) => (
            <div
              key={r.id}
              onClick={() => navigate(`/commercial-entities/${r.id}`)}
              title={`Open ${r.name}`}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, padding: '3px 0', cursor: 'pointer' }}
            >
              <span style={ELLIPSIS}>
                {r.name}
                {r.organisations?.length > 0 && <span style={{ color: '#888' }}>, {r.organisations.join(', ')}</span>}
              </span>
              <span style={{ fontWeight: 600, flexShrink: 0 }}>{r.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="metrics-section" style={{ marginBottom: 0 }}>
      <h2>Activities</h2>
      <div style={CARD_STYLE}>
        {/* View all sits at the end of the pills row, so the period toggle
            below never moves when switching pills. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minHeight: 40 }}>
          <FilterChips
            options={ACTIVITY_TYPES.map((t) => ({ key: t.key, label: t.label, count: typeTotals[t.key] }))}
            value={type}
            onChange={setType}
            ariaLabel="Activity type"
            className="dashboard-filters"
          />
          {/* On every pill, keeping its setting as you move between pills
              and periods; Meetings have no direction, so it's greyed out. */}
          <ToggleSwitch
            checked={showInOut && !!meta.splitSegments}
            disabled={!meta.splitSegments}
            onChange={setShowInOut}
            label={<span style={{ fontSize: '0.85rem', fontWeight: 600 }}>In / out</span>}
            title={meta.splitSegments ? 'Split into in and out' : 'Meetings have no in or out'}
          />
          <button
            type="button"
            className="dashboard-refresh-btn"
            style={{ marginLeft: 'auto' }}
            disabled={!summary}
            onClick={() => summary && setDrill({ from: summary.daily[0].date, to: summary.daily[summary.daily.length - 1].date, label: `last ${summary.days} days` })}
            title={`Open the list of ${meta.noun} for the period`}
          >
            View all
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <ScopeTabs
            scope={scope}
            onChange={setScope}
            noun={meta.noun}
            extra={<PeriodToggle value={period} onChange={setPeriod} />}
          />
        </div>
        {content}
      </div>
      <ActivityReviewDrawer
        open={!!drill}
        onOpenChange={(isOpen) => { if (!isOpen) setDrill(null); }}
        initialScope={scope}
        type={type}
        range={drill}
        onRemoved={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}

function CrmDashboard() {
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setLastRefreshedAt(new Date());
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="dashboard dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-content-inner">
          <div className="dashboard-page-header">
            <div className="dashboard-page-header-main">
              <h2 className="dashboard-page-title">CRM Dashboard</h2>
              <p className="dashboard-page-description">The Pipeline, what's coming up, and Activities across every record.</p>
            </div>
            <div className="dashboard-page-controls">
              <span className="dashboard-page-meta">
                <Clock3 size={14} /> Last refresh: {formatTimeAU(lastRefreshedAt)}
              </span>
              <button className="dashboard-refresh-btn" type="button" onClick={handleRefresh}>Refresh</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24, marginBottom: 24 }}>
            <PipelineCard refreshKey={refreshKey} />
            <UpcomingCard refreshKey={refreshKey} />
          </div>
          <ActivitiesCard refreshKey={refreshKey} />
        </div>
      </div>
    </div>
  );
}

export default CrmDashboard;
